import * as Needle from '@neo4j-ndl/react';
import { CubeTransparentIconOutline, KeyIconOutline } from '@neo4j-ndl/react/icons';
import type { SSOProviderOriginal } from '@nx/constants';
import type { ParseableError } from '@nx/errors';
import { useCapability } from '@nx/state';
import type { FieldError } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';

import ConnectionMessage from '../connection-form/form-elements/connection-error';
import { useNeo4jDiscovery } from '../hooks/use-neo4j-discovery';
import { PasswordInput } from '../password-input';
import { ConnectionUrlInput } from './connection-url-input';

interface FormInputs {
  enableProjectSharing: boolean;
  name: string;
  password: string;
  url: string;
  username: string;
}

export type ConnectionPayload = { type: 'basic' | 'sso'; inputs: FormInputs } & (
  | { type: 'basic' }
  | {
      type: 'sso';
      provider: SSOProviderOriginal;
    }
);

export type ConnectionCallback = (payload: ConnectionPayload, event?: React.BaseSyntheticEvent) => Promise<void>;

export type ConnectionNotification =
  | {
      type: 'warning';
      value: string;
    }
  | { type: 'error'; value: string }
  | {
      type: 'driver-error';
      discoveryUrls?: string[];
      error?: ParseableError;
    };

interface ExternalConnectionModalProps {
  allowPasswordReveal?: boolean;
  allowUrlChange?: boolean;
  autoFocus?: boolean;
  defaultValues: Partial<FormInputs>;
  isDisabled?: boolean;
  isExternal?: boolean;
  message?: ConnectionNotification;
  onChange?: (value: FormInputs['url']) => void;
  onClose: () => void;
  onConnect: ConnectionCallback;
}

function toErrorText(error?: FieldError) {
  if (error === undefined) {
    return undefined;
  }

  if (error.message !== '') {
    return error.message;
  }

  if (error.type === 'required') {
    return 'Required field';
  }

  return undefined;
}

function SsoProviders(props: { debounce?: boolean; url: string; onConnect: (provider: SSOProviderOriginal) => void }) {
  const discoveryData = useNeo4jDiscovery(props.url, { debounce: props.debounce === true ? 1000 : 0 });

  if (discoveryData.status === 'idle') {
    return (
      <Needle.Typography className="italic" variant="body-medium">
        Not available
      </Needle.Typography>
    );
  }

  if (discoveryData.status === 'pending') {
    return (
      <Needle.Button
        as="div"
        className="w-full justify-start rounded-lg"
        size="large"
        color="neutral"
        fill="outlined"
        isDisabled
        isLoading
      >
        <Needle.Typography variant="body-medium" className="text-palette-neutral-text-default ml-6">
          Loading SSO configuration
        </Needle.Typography>
      </Needle.Button>
    );
  }

  if (discoveryData.status === 'rejected') {
    <div>
      <Needle.Banner type="danger" title="Failed to load SSO configuration" />
    </div>;
  }

  const providers = discoveryData.data?.SSOProviders ?? [];

  if (providers.length === 0) {
    return (
      <Needle.Button
        as="div"
        className="w-full justify-start rounded-lg opacity-50"
        size="large"
        color="neutral"
        fill="outlined"
        isDisabled
      >
        <CubeTransparentIconOutline className="text-palette-neutral-icon h-6 w-6" />
        <Needle.Typography variant="body-medium" className="text-palette-neutral-text-default ml-4">
          No SSO providers found
        </Needle.Typography>
      </Needle.Button>
    );
  }

  return (
    <div>
      {(discoveryData.data?.SSOProviders ?? []).map((provider) => (
        <Needle.Button
          className="w-full justify-start rounded-lg"
          size="large"
          color="primary"
          fill="outlined"
          key={provider.name}
          onClick={() => props.onConnect(provider)}
        >
          <KeyIconOutline className="text-palette-primary-icon h-6 w-6" />
          <Needle.Typography variant="body-medium" className="text-palette-primary-text-default ml-4">
            Connect with {provider.name}
          </Needle.Typography>
        </Needle.Button>
      ))}
    </div>
  );
}

export function ExternalConnectionModal(props: ExternalConnectionModalProps) {
  const { control, formState, handleSubmit, register, setValue } = useForm<FormInputs>({
    defaultValues: props.defaultValues,
  });
  const [ssoEnabled] = useCapability('framework:sso-db-connection');

  const urlField = register('url', { required: true });
  const nameField = register('name');
  const usernameField = register('username');
  const passwordField = register('password');

  const url = useWatch({ control, name: urlField.name });

  const onSubmit = handleSubmit((inputs, event) => props.onConnect({ type: 'basic', inputs }, event));

  return (
    <Needle.Dialog
      isOpen
      size="unset"
      modalProps={{ className: 'max-w-[480px] overflow-visible mx-auto mt-12 p-10 align-top' }}
      onClose={props.onClose}
    >
      <Needle.Dialog.Header>
        Connect to{' '}
        <span className={props.defaultValues.name === undefined ? '' : 'font-normal'}>
          {props.defaultValues.name ?? 'Neo4j'}
        </span>
      </Needle.Dialog.Header>
      <Needle.Dialog.Content className="flex flex-col gap-4">
        {props.message !== undefined && props.message.type === 'driver-error' && (
          <div className="mb-2">
            <ConnectionMessage
              show
              error={props.message.error}
              discoveryUrls={props.isExternal === true ? props.message.discoveryUrls : []}
              onDiscoveryUrlSelected={(discoveryUrl) => setValue(urlField.name, discoveryUrl)}
            />
          </div>
        )}
        {props.message !== undefined && (props.message.type === 'warning' || props.message.type === 'error') && (
          <Needle.Banner
            hasIcon
            type={props.message.type === 'error' ? 'danger' : props.message.type}
            description={<Needle.Typography variant="body-medium">{props.message.value}</Needle.Typography>}
            className="mb-2 flex items-center"
          />
        )}
        <div className="flex flex-col gap-2">
          <ConnectionUrlInput
            {...urlField}
            errorText={toErrorText(formState.errors.url)}
            isReadOnly={props.allowUrlChange !== true}
            showConnectionMethods={props.allowUrlChange === true}
            value={url}
            onValueChange={(value) => setValue(urlField.name, value)}
          />
        </div>
        {props.allowUrlChange === true ? (
          <Needle.TextInput
            {...nameField}
            isFluid
            size="medium"
            placeholder={url}
            label="Connection alias"
            htmlAttributes={{
              name: nameField.name,
            }}
          />
        ) : null}
        {props.allowUrlChange === true ? <Needle.Checkbox label="Share with project members" /> : null}
        {!ssoEnabled || url === '' ? null : (
          <div className="flex w-full flex-col gap-2">
            <Needle.Typography variant="h6">Single sign-on</Needle.Typography>
            <SsoProviders
              debounce={props.allowUrlChange}
              url={url}
              onConnect={(provider) => {
                const connect = handleSubmit((inputs) => props.onConnect({ type: 'sso', inputs, provider }));
                void connect();
              }}
            />
          </div>
        )}
        <Needle.Divider />
        <div className="flex w-full flex-col gap-2">
          <Needle.Typography variant="h6">Database credentials</Needle.Typography>
          <Needle.TextInput
            {...usernameField}
            size="medium"
            isFluid
            label="Database user"
            placeholder="neo4j"
            htmlAttributes={{
              'aria-label': 'Database user',
              name: usernameField.name,
            }}
          />
          <PasswordInput
            {...passwordField}
            allowReveal={props.allowPasswordReveal}
            size="medium"
            isFluid
            label="Password"
            htmlAttributes={{
              autoFocus: props.allowUrlChange !== true && props.autoFocus === true,
              'aria-label': 'Password',
              name: passwordField.name,
            }}
          />
        </div>
        <Needle.Button
          type="submit"
          className="mt-3 w-full"
          isDisabled={props.isDisabled === true}
          onClick={() => {
            void onSubmit();
          }}
        >
          Connect
        </Needle.Button>
      </Needle.Dialog.Content>
    </Needle.Dialog>
  );
}
